import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Box, Button, TextField, InputAdornment, Typography,
  Chip, Tooltip, IconButton, Avatar, Switch,
} from "@mui/material";
import {
  useDeleteSmsTemplateMutation,
  useGetSmsTemplatesQuery,
  useToggleSmsTemplateMutation,
  useDuplicateSmsTemplateMutation,
} from "../../../Api/attoDeskApi";
import { ISmsTemplate } from "../../../Api/Interface/api.interface";
import SmsEditDialog from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import Pagination from "../../../Components/Pagination";

// SMS segment calculator
const smsSegments = (len: number) => {
  if (len === 0) return { segments: 0, remaining: 160 };
  if (len <= 160) return { segments: 1, remaining: 160 - len };
  const segs = Math.ceil(len / 153);
  return { segments: segs, remaining: segs * 153 - len };
};

const CATEGORY_STYLE: Record<string, { label: string; bg: string; color: string; border: string }> = {
  client: { label: "Client",  bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  driver: { label: "Driver",  bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  both:   { label: "Both",    bg: "#fdf4ff", color: "#2f3891", border: "#e9d5ff" },
};

const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery]         = useState("");
  const [currentSearch, setCurrentSearch]     = useState("");
  const [page, setPage]                       = useState(0);
  const [rowsPerPage]                         = useState(25);
  const [selected, setSelected]               = useState<ISmsTemplate | null>(null);
  const [openEdit, setOpenEdit]               = useState(false);
  const [toDelete, setToDelete]               = useState<ISmsTemplate | null>(null);
  const [openDelete, setOpenDelete]           = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading, isError, refetch } = useGetSmsTemplatesQuery({ searchText: currentSearch });

  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === "") {
      setCurrentSearch(debouncedSearch);
      refetch();
    }
  }, [debouncedSearch]);

  const [deleteTemplate]    = useDeleteSmsTemplateMutation();
  const [toggleTemplate]    = useToggleSmsTemplateMutation();
  const [duplicateTemplate] = useDuplicateSmsTemplateMutation();

  const handleDelete = async (id: string) => {
    try {
      const res = await deleteTemplate(id).unwrap();
      if (res.status) { showMessage("Template deleted"); setOpenDelete(false); }
      else showErrorMessage(res.message || "Failed to delete");
    } catch { showErrorMessage("Failed to delete template"); }
  };

  const handleToggle = async (row: ISmsTemplate) => {
    try {
      const res = await toggleTemplate({ id: row.id }).unwrap();
      if (res.status) showMessage(res.message || "Updated");
      else showErrorMessage(res.message || "Failed");
    } catch { showErrorMessage("Failed to toggle template"); }
  };

  const handleDuplicate = async (row: ISmsTemplate) => {
    try {
      const res = await duplicateTemplate({ id: row.id }).unwrap();
      if (res.status) showMessage(res.message || "Duplicated");
      else showErrorMessage(res.message || "Failed");
    } catch { showErrorMessage("Failed to duplicate template"); }
  };

  const templates: ISmsTemplate[] = Array.isArray(data?.data) ? (data!.data as ISmsTemplate[]) : [];
  const filtered = templates.filter(t => t.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  const paged    = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const thSx = {
    fontWeight: 700, fontSize: 11, color: "#64748b",
    textTransform: "uppercase" as const, letterSpacing: "0.06em",
    py: 1.5, px: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc",
  };
  const tdSx = { py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: "linear-gradient(135deg,#0ea5e9,#2f3891)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <SmsOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
              SMS Templates
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
              {filtered.length} template{filtered.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search templates…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ color: "#94a3b8", fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 13, bgcolor: "#fff", minWidth: 220 },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/sms/create")}
            sx={{
              background: "linear-gradient(135deg,#0ea5e9,#2f3891)",
              borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13, px: 2.5,
              boxShadow: "0 2px 8px rgba(14,165,233,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#0284c7,#2f3891)" },
            }}
          >
            Add Template
          </Button>
        </Box>
      </Box>

      {/* ── Table card ───────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#0ea5e9" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>#</TableCell>
                  <TableCell sx={thSx}>Template</TableCell>
                  <TableCell sx={thSx}>Category</TableCell>
                  <TableCell sx={thSx}>Preview</TableCell>
                  <TableCell sx={thSx}>Length</TableCell>
                  <TableCell sx={thSx}>Active</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <SmsOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                        <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                          {isError ? "Error fetching templates" : searchQuery ? "No templates match your search" : "No SMS templates yet"}
                        </Typography>
                        {!isError && !searchQuery && (
                          <Button
                            variant="outlined" size="small"
                            onClick={() => navigate("/sms/create")}
                            sx={{ mt: 2, borderRadius: 2, textTransform: "none", color: "#0ea5e9", borderColor: "#0ea5e9" }}
                          >
                            Create first template
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((row, index) => {
                    const initials = row.name?.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
                    const cat      = CATEGORY_STYLE[row.category] ?? CATEGORY_STYLE.client;
                    const { segments, remaining } = smsSegments(row.message?.length || 0);
                    const isOverLimit = (row.message?.length || 0) > 160;

                    return (
                      <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#f8fbff" }, "&:last-child td": { borderBottom: 0 } }}>
                        {/* # */}
                        <TableCell sx={{ ...tdSx, color: "#94a3b8", fontSize: 12, width: 48 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        {/* Template name */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 700, background: "linear-gradient(135deg,#0ea5e9,#2f3891)" }}>
                              {initials}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
                                {row.name}
                              </Typography>
                              {row.is_system && (
                                <Chip
                                  label="System"
                                  size="small"
                                  sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", mt: 0.3 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Category */}
                        <TableCell sx={tdSx}>
                          <Chip
                            label={cat.label}
                            size="small"
                            sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: cat.bg, color: cat.color, border: `1px solid ${cat.border}` }}
                          />
                        </TableCell>

                        {/* Preview */}
                        <TableCell sx={tdSx}>
                          <Typography sx={{ fontSize: 12, color: "#94a3b8", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.message ? row.message.slice(0, 80) + (row.message.length > 80 ? "…" : "") : "—"}
                          </Typography>
                        </TableCell>

                        {/* Length */}
                        <TableCell sx={tdSx}>
                          <Tooltip title={`${segments} SMS part${segments !== 1 ? "s" : ""}, ${remaining} chars remaining`} arrow>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: isOverLimit ? "#ef4444" : "#475569" }}>
                                {row.message?.length || 0}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                                /{segments > 1 ? `${segments}×153` : "160"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>

                        {/* Active toggle */}
                        <TableCell sx={tdSx}>
                          <Tooltip title={row.is_active ? "Disable template" : "Enable template"} arrow>
                            <Switch
                              checked={row.is_active}
                              onChange={() => handleToggle(row)}
                              size="small"
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#0ea5e9" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#0ea5e9" },
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title="Edit template" arrow>
                              <IconButton
                                size="small"
                                onClick={() => { setSelected(row); setOpenEdit(true); }}
                                sx={{ color: "#0ea5e9", bgcolor: "#e0f2fe", borderRadius: 1.5, width: 30, height: 30, "&:hover": { bgcolor: "#bae6fd" } }}
                              >
                                <EditSharpIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate template" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDuplicate(row)}
                                sx={{ color: "#2f3891", bgcolor: "#f5f3ff", borderRadius: 1.5, width: 30, height: 30, "&:hover": { bgcolor: "#ede9fe" } }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            {!row.is_system && (
                              <Tooltip title="Delete template" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => { setToDelete(row); setOpenDelete(true); }}
                                  sx={{ color: "#ef4444", bgcolor: "#fef2f2", borderRadius: 1.5, width: 30, height: 30, "&:hover": { bgcolor: "#fee2e2" } }}
                                >
                                  <DeleteOutlineSharpIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filtered.length > rowsPerPage && (
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
            <Pagination count={filtered.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </Box>

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}
      {selected && (
        <SmsEditDialog
          open={openEdit}
          onClose={() => { setSelected(null); setOpenEdit(false); }}
          data={selected}
        />
      )}
      {toDelete && (
        <DeletePopup
          open={openDelete}
          handleCloseDelete={() => { setToDelete(null); setOpenDelete(false); }}
          onConfirm={async () => { await handleDelete(toDelete.id.toString()); }}
          title="Delete SMS Template"
          content={`Are you sure you want to delete "${toDelete.name}"?`}
        />
      )}
    </Box>
  );
};

export default Index;
